// 体験チケット (#1248) の API。SpotSheet からの購入 → 予約一覧 → QR →
// 店舗スタッフ消し込み。配置ルールは services/api/index.ts のヘッダ参照。
import { apiFetch } from '../../services/api/_client'
import type { RefundAlternateMethod, RefundVoucher } from '../../services/api/reservations'

// ---- 型定義 ----
/** #1522 送迎つき体験の集合場所 (店が商品ごとに候補を登録し、客が購入時に選ぶ) */
export interface MeetingPoint {
  id: string
  name: string
  address?: string
  /** 店が貼った地図 URL。無ければ住所/名前で Google Maps を検索する */
  mapUrl?: string
}

export interface ExperienceProduct {
  id: string
  storeId: string
  name: string
  nameEn: string
  description: string
  descriptionEn: string
  imageUrl: string
  priceJpy: number
  genre: string
  linkedSpotId?: string | null
  redeemPolicy: string
  /** #1482 送迎付きガイドか。送迎付き = timed + これが true */
  transportIncluded?: boolean
  /** #1522 集合場所の候補 (送迎つき)。空なら購入時に選ばない */
  meetingPoints?: MeetingPoint[]
  /** #1494 代理販売の提供元名（空 = 直販）。アプリに登録していない相手なので、
   *  販売者が書く表示情報として持つ（入金先でも承認先でもない） */
  providerName?: string
  providerNameEn?: string
  /** #1487 所要時間(分)。timed は未設定なら枠の長さが入る。free_entry は未設定なら null */
  durationMinutes?: number | null
  /** #1487 集合だけ決めて滞在は自由か。true なら終了時刻を出さない */
  openEnded?: boolean
  /** #1556 現地払いを選べるか。**店が商品ごとに決める**（既定 false）。
   *  無断キャンセルの損失を負うのは店なので、客側では変えられない。 */
  onsitePaymentEnabled?: boolean
}

export interface ExperienceTicket {
  id: string
  status: string          // pending | issued | redeemed | void
  paymentStatus: string   // pending | paid | failed | refunded
  /** #1800 何で払ったか。ポイント払いは 'points' (改名前の 'jpyc' も。lib/paymentMethods の isPointsMethod で見る) */
  paymentMethod?: string
  storeId: string
  productId?: string
  productName: string
  productNameEn: string
  priceJpy: number
  quantity?: number       // 何名分 (#1292)
  redeemPolicy?: string   // free_entry | timed
  /** 枠 (#1292): timed のみ日時が入る。free_entry は null (= 時間指定なし) */
  slotDate?: string | null       // YYYY-MM-DD
  slotStartTime?: string | null  // HH:MM
  /** 購入文脈 (#1291): どのスポット/旅程/プラン用か */
  spotId?: string | null
  tripId?: string | null
  planId?: string | null
  purchasedAt: string | null
  redeemedAt: string | null
  /** 支払い済みのみ。QR に埋める消し込みコード */
  code?: string | null
  /** 店舗側 lookup/redeem view にのみ含まれる */
  redeemedBy?: string | null
  /** 店舗側のみ。qr = 客の端末を読んだ / manual = 手入力 (#1520 follow-up) */
  redeemMethod?: 'qr' | 'manual' | null
  /** #1291: 公開共有 URL 用の非秘匿 ID (発行済みチケットのみ) */
  shareCode?: string | null
  /** #1556 prepaid（前払い）| onsite（現地払い）。onsite は当日その場で払う */
  paymentTiming?: 'prepaid' | 'onsite'
  /** online | onsite。現地で買ったものは「その場で参加済み」なので履歴の見え方が違う */
  channel?: string
  /** #1482 送迎付きか (購入時に商品から券へ写される) */
  transportIncluded?: boolean
  /** #1494 代理販売の提供元名（空 = 直販）。アプリに登録していない相手なので、
   *  販売者が書く表示情報として持つ（入金先でも承認先でもない） */
  providerName?: string
  providerNameEn?: string
  /** #1487 購入時点の所要時間(分)。後から店が枠を変えても、売った券は動かない */
  durationMinutes?: number | null
  openEnded?: boolean
  /** #1487 終了時刻 HH:MM。滞在自由 (openEnded) の券は null */
  slotEndTime?: string | null
  /** #1483 送迎の進み具合。null = まだ動いていない | heading | arrived */
  transportStatus?: string | null
  /** #1483 到着目安 (分)。ガイドが入力し、待っている客に見せる */
  transportEtaMinutes?: number | null
  transportStatusAt?: string | null
  /** #1486 部分対応: 車のナンバー。**客が「この車で合っているか」を確かめる手段** */
  transportVehiclePlate?: string | null
  /** #1486 部分対応: 担当者の表示名。店が名簿に入れた文字列そのまま
   *  (フルネームか姓かは店の判断 — 入力欄に「客の画面にも出ます」と明示している) */
  transportAssigneeName?: string | null
  /** #1522 到着予定時刻 (ISO)。押した時刻 + 目安。端末側で残り分数を減らす */
  transportEtaAt?: string | null
  /** #1522 担当者の業務用電話。送迎中 (heading / arrived) の間だけサーバが返す */
  transportAssigneePhone?: string | null
  /** #1522 購入時に客が選んだ集合場所 (スナップショット) */
  meetingPoint?: MeetingPoint | null
  /** #1498 いま自分で取り消せるか。**規定の判断はサーバがする** — 画面側で条件を
   * 組み直すと、出したボタンが押した瞬間に断られる */
  cancellable?: boolean
  /** #1498 取り消したときに請求される額 (円)。0 なら無料 */
  cancelChargeJpy?: number
  /** #1498 無料になる理由。'store_no_show' = ガイドが来ていない (店舗過失)。
   * 「まだ先なので無料」とは客にとって別の話なので出し分ける */
  cancelFreeReason?: string | null
  /** #1498 取り消し時に実際に頂いた額 (円)。0/未設定なら請求していない */
  cancellationChargeJpy?: number | null
  /** #1303 取り消し理由。'store_cancelled' = 店舗都合の催行中止 (全額返金)。
   * union で持つ — 新しい理由を足すときに表示側の分岐判断を強制する */
  voidReason?:
    | 'store_cancelled'
    | 'customer_cancelled'
    | 'store_no_show'
    | 'customer_no_show'
    | 'transport_no_show'
    | null
  /** #1303 店舗が入力した中止理由 (悪天候等)。催行中止の券のみ */
  storeCancelReason?: string | null
  /** #1303 お金の結末。客に「与信か請求か」の場合分けを読ませず、サーバが
   * 断定した結果で文言を出し分ける (refunded=全額返金 / released=請求前取消 / none=支払いなし) */
  cancelOutcome?: 'refunded' | 'released' | 'none' | null
  /** #2298 券の返金の記録 (services/pay/api/domains/experience/refund_record.py)。
   * 'failed' = 返金がカード会社で通らなかった / 'alternate' = お店が別の方法で返した */
  refundStatus?: 'succeeded' | 'failed' | 'alternate' | null
  /** #2298 まだ返っていない額 (円)。記録の無い古い券は null */
  refundOutstanding?: number | null
  /** #2298 カードの解約などで同じカードには返せない (お店で引換券を見せて返してもらう) */
  refundCardUnreachable?: boolean
  /** #2476 申し立て (チャージバック) が来ている。カード会社から戻るので引換券の案内を出さない */
  refundDisputed?: boolean
  /** #2298 引換券ができている (毎朝のジョブが出す。まだなら翌朝) */
  refundVoucherReady?: boolean
  /** #2298 お店が別の方法で返した (引換券の引き換え) */
  refundAlternate?: { method: RefundAlternateMethod | null; amount: number; recordedAt: string | null } | null
}

/** #1291: 公開共有ページ (`/e/:shareCode`) が表示する予約内容の要約 (PII / code なし) */
export interface PublicExperienceShare {
  productName: string
  productNameEn: string
  priceJpy: number
  /** 同一顧客 × 同一商品の支払い済み枚数 (= 何名分) */
  count: number
  /** うち消し込み済み枚数 */
  redeemedCount: number
  redeemPolicy: string
  /** timed のみ (#1292)。free_entry は null (= 時間指定なし) */
  slotDate?: string | null
  slotStartTime?: string | null
  store: {
    id: string
    name: string | null
    address: string | null
    googleMapsUrl: string | null
    phoneNumber: string | null
  }
}

/** #1248: `/t/:code` (メール/紙印刷) の公開券面。code の所持を認可とみなして
 * 消し込み用 code (QR の値) を含む。PII (memberId / 決済 ID 等) は含まない */
export interface PublicExperienceTicket {
  code: string
  status: string // issued | redeemed (void は 404)
  /** #1556: 現地払いの券は「当日その場で払う」を券面にも明示する */
  paymentTiming: 'prepaid' | 'onsite'
  productName: string
  productNameEn: string
  providerName: string
  providerNameEn: string
  priceJpy: number
  quantity: number
  /** timed のみ (#1292)。free_entry は null (= 時間指定なし) */
  slotDate?: string | null
  slotStartTime?: string | null
  slotEndTime?: string | null
  meetingPoint?: MeetingPoint | null
  redeemedAt?: string | null
  store: PublicExperienceShare['store']
}

/** #1292: 週次スケジュールから導出された空き枠 (顧客の枠選択用。残数は remaining に集約) */
export interface ExperienceSlot {
  date: string        // YYYY-MM-DD (JST)
  startTime: string   // HH:MM (JST)
  /** #1487 終了時刻 HH:MM。滞在自由の商品は null */
  endTime?: string | null
  capacity: number
  remaining: number
  soldOut: boolean
}

export interface TicketPaymentIntent {
  clientSecret: string
  paymentIntentId: string
  connectedAccountId: string
  amount: number
  /**
   * #1498 与信 (manual) か即時売上 (automatic) か。
   * StripeCheckout は manual のとき `requires_capture` を成功として扱う。
   * **渡し忘れると与信成功が「決済システムに障害」になる**ので必須。
   */
  captureMethod: 'automatic' | 'manual'
}

const jsonPost = (body: unknown): RequestInit => ({
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
})

export const experienceApi = {
  // --- 顧客: 購入導線 ---
  listExperienceProducts: async (params: { spotId?: string; storeId?: string }): Promise<{ products: ExperienceProduct[] }> => {
    const q = new URLSearchParams()
    if (params.spotId) q.set('spotId', params.spotId)
    if (params.storeId) q.set('storeId', params.storeId)
    const res = await apiFetch(`/experience/products?${q.toString()}`)
    return res.json()
  },

  getExperienceProduct: async (productId: string): Promise<ExperienceProduct> => {
    const res = await apiFetch(`/experience/products/${encodeURIComponent(productId)}`)
    return res.json()
  },

  createExperienceTicket: async (
    productId: string,
    context?: {
      tripId?: string | null; planId?: string | null
      slotDate?: string | null; slotStartTime?: string | null
      quantity?: number; meetingPointId?: string | null
      /** #1556 'prepaid'（既定）| 'onsite'。**onsite は店が許可した商品のみ**通る */
      paymentTiming?: 'prepaid' | 'onsite'
      /** #1800 前払いの手段。**枠を押さえる前に**店がその手段を受けるか見るので作成時に渡す */
      paymentMethod?: 'card' | 'points' | 'trial_mock'
    },
  ): Promise<ExperienceTicket> => {
    const res = await apiFetch('/experience/tickets', jsonPost({ productId, ...context }))
    return res.json()
  },

  // #1800 体験チケットを YOKOSO ポイントで払う。成功した時点で券は発行済み
  // (カードのような confirm 段は無い)。残高不足分をその場でチャージするときだけ topup。
  payExperienceTicketWithPoints: async (
    ticketId: string, topup?: { topupAmount: number; topupFeeJpyc: number },
  ): Promise<ExperienceTicket & { transactionId: string; newBalance: number }> => {
    const res = await apiFetch(
      `/experience/tickets/${encodeURIComponent(ticketId)}/points-checkout`,
      jsonPost(topup || {}),
    )
    return res.json()
  },

  // #2140 体験の店の券を模擬カードで払ったことにして発行する (本物の決済は通さない。体験の店だけ)
  trialMockPayExperienceTicket: async (ticketId: string): Promise<ExperienceTicket> => {
    const res = await apiFetch(
      `/experience/tickets/${encodeURIComponent(ticketId)}/trial-mock-pay`,
      { method: 'POST' },
    )
    return res.json()
  },

  // #1556 現地払いの券を発行する（決済を通さない）。前払いは
  // /payment-intent → 決済 → webhook で発行されるが、現地払いは当日その場で払うので
  // 入金を待っていたら券が発行されない。
  reserveOnsiteExperienceTicket: async (ticketId: string): Promise<ExperienceTicket> => {
    const res = await apiFetch(
      `/experience/tickets/${encodeURIComponent(ticketId)}/reserve-onsite`,
      { method: 'POST' },
    )
    return res.json()
  },

  // #1292: 未払いチケットのキャンセル/離脱で枠を即時開放 (在庫リーク防止)。best-effort。
  voidExperienceTicket: async (ticketId: string): Promise<void> => {
    try {
      await apiFetch(`/experience/tickets/${encodeURIComponent(ticketId)}/void`, { method: 'POST' })
    } catch { /* 開放失敗は HOLD_MINUTES で自己修復するので握りつぶす */ }
  },

  // #1488 旅行者が自分で予約を取り消す (出発前は全額返金)。
  // **「お店に電話してください」は訪日外国人には機能しない** ので、アプリで完結させる。
  // 409 = 既にガイドが向かっている / 開始時刻を過ぎている (ApiError で理由を受ける)。
  cancelExperienceTicket: async (ticketId: string): Promise<ExperienceTicket> => {
    const res = await apiFetch(`/experience/tickets/${encodeURIComponent(ticketId)}/cancel`, { method: 'POST' })
    return res.json()
  },

  // #2298 自分の券の返金の引換券 (本人だけ)。お店で見せる QR とコード。404 = まだ無い (翌朝)
  getMyTicketRefundVoucher: async (ticketId: string): Promise<RefundVoucher> => {
    const res = await apiFetch(`/experience/tickets/${encodeURIComponent(ticketId)}/refund-voucher`)
    return res.json()
  },

  // #1292: timed 商品の空き枠 (公開・残数付き)。週次スケジュールから導出される。
  listExperienceSlots: async (productId: string): Promise<{ slots: ExperienceSlot[] }> => {
    const res = await apiFetch(`/experience/products/${encodeURIComponent(productId)}/slots`)
    return res.json()
  },

  createExperienceTicketPaymentIntent: async (ticketId: string): Promise<TicketPaymentIntent> => {
    const res = await apiFetch(`/experience/tickets/${encodeURIComponent(ticketId)}/payment-intent`, { method: 'POST' })
    return res.json()
  },

  // 決済成功直後にクライアントが叩く同期 confirm (webhook を待たず即発行)。
  // サーバが Stripe に PI を retrieve して検証してから発行する。
  confirmExperienceTicket: async (ticketId: string): Promise<ExperienceTicket> => {
    const res = await apiFetch(`/experience/tickets/${encodeURIComponent(ticketId)}/confirm`, { method: 'POST' })
    return res.json()
  },

  getMyExperienceTickets: async (): Promise<{ tickets: ExperienceTicket[] }> => {
    const res = await apiFetch('/experience/tickets/me')
    return res.json()
  },

  getExperienceTicket: async (ticketId: string): Promise<ExperienceTicket> => {
    const res = await apiFetch(`/experience/tickets/${encodeURIComponent(ticketId)}`)
    return res.json()
  },

  // 共有 URL 用の shareCode を確保 (発行時採番済みなら既存、旧チケットは後追い採番)。
  ensureExperienceShareCode: async (ticketId: string): Promise<{ shareCode: string }> => {
    const res = await apiFetch(`/experience/tickets/${encodeURIComponent(ticketId)}/share-code`, { method: 'POST' })
    return res.json()
  },

  // 公開共有ページ (未ログイン可)。予約内容の要約を返す。
  getPublicExperienceShare: async (shareCode: string): Promise<PublicExperienceShare> => {
    const res = await apiFetch(`/experience/share/${encodeURIComponent(shareCode)}`)
    return res.json()
  },

  // #1248: 公開券面 (未ログイン可・メール/紙印刷)。code 所持を認可とみなし QR 値を返す。
  getPublicExperienceTicket: async (code: string): Promise<PublicExperienceTicket> => {
    const res = await apiFetch(`/experience/tickets/code/${encodeURIComponent(code)}`)
    return res.json()
  },

  // --- 店舗スタッフ: 消し込み (owner/staff トークンで認証) ---
  lookupExperienceTicket: async (code: string): Promise<ExperienceTicket> => {
    const res = await apiFetch(`/experience/store/tickets/${encodeURIComponent(code)}`)
    return res.json()
  },

  // method = どうやってコードを得たか。送迎ありでは消し込みが請求の確定なので、
  // 客の端末を読んだ (qr) か手入力 (manual) かを券に残す (#1520 follow-up)
  redeemExperienceTicket: async (code: string, method: 'qr' | 'manual' = 'manual'): Promise<ExperienceTicket> => {
    const res = await apiFetch(`/experience/store/tickets/${encodeURIComponent(code)}/redeem`, jsonPost({ method }))
    return res.json()
  },

  // --- 店舗オーナー: 体験商品 + 枠の設定 (#1292, owner/manager トークン) ---
  listStoreExperienceProducts: async (storeId: string): Promise<{ products: OwnerExperienceProduct[] }> => {
    const res = await apiFetch(`/experience/store/${encodeURIComponent(storeId)}/products`)
    return res.json()
  },

  createStoreExperienceProduct: async (storeId: string, data: ExperienceProductInput): Promise<OwnerExperienceProduct> => {
    const res = await apiFetch(`/experience/store/${encodeURIComponent(storeId)}/products`, jsonPost(data))
    return res.json()
  },

  updateStoreExperienceProduct: async (storeId: string, productId: string, data: Partial<ExperienceProductInput>): Promise<OwnerExperienceProduct> => {
    const res = await apiFetch(`/experience/store/${encodeURIComponent(storeId)}/products/${encodeURIComponent(productId)}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data),
    })
    return res.json()
  },

  // #1292: 週次スケジュール (曜日 on/off + 営業時間 + 1枠の長さ + 定員) の取得/保存。
  // 枠は materialize せず、これを真実に空き枠を導出する。
  getStoreExperienceSchedule: async (storeId: string, productId: string): Promise<ExperienceSchedule> => {
    const res = await apiFetch(`/experience/store/${encodeURIComponent(storeId)}/products/${encodeURIComponent(productId)}/schedule`)
    return res.json()
  },

  setStoreExperienceSchedule: async (storeId: string, productId: string, schedule: ExperienceSchedule): Promise<ExperienceSchedule> => {
    const res = await apiFetch(`/experience/store/${encodeURIComponent(storeId)}/products/${encodeURIComponent(productId)}/schedule`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(schedule),
    })
    return res.json()
  },

  // --- 店舗の当日運営 (#1292 Phase 2): 空き状況 / 現地販売 / オンライン締切 ---
  listStoreExperienceStoreSlots: async (storeId: string, productId: string): Promise<{ slots: StoreExperienceSlot[] }> => {
    const res = await apiFetch(`/experience/store/${encodeURIComponent(storeId)}/products/${encodeURIComponent(productId)}/store-slots`)
    return res.json()
  },

  // slotDate/slotStartTime は timed のみ (free_entry は不要)
  // #2190 idempotencyKey: 1 回の販売ごとに画面が作る鍵 (同じ鍵の押し直しは 1 回目のチケットを返す)
  createOnsiteSale: async (storeId: string, productId: string, spec: { slotDate?: string; slotStartTime?: string; quantity: number; paymentMethod: string; idempotencyKey?: string }): Promise<{ id: string; quantity: number; claimCode?: string }> => {
    const res = await apiFetch(`/experience/store/${encodeURIComponent(storeId)}/products/${encodeURIComponent(productId)}/onsite-sale`, jsonPost(spec))
    return res.json()
  },

  // 現地で買ったチケットを自分のアカウントに紐づける (#1248 follow-up)。
  // 現地販売はその場で完結していて売上・在庫は成立済み。これは購入履歴・評価・
  // シェアを使えるようにするためだけの導線で、**当日限り**。
  claimOnsiteTicket: async (code: string): Promise<ExperienceTicket> => {
    const res = await apiFetch('/experience/tickets/claim', jsonPost({ code }))
    return res.json()
  },

  // #1293 現地カード決済: pending チケットで枠を確保し、店頭リーダー (S700) に金額を送る。
  // 返り値の paymentIntentId を共通の Terminal ポーリング (TerminalChargeModal) に渡して
  // 確定させる。確定で paid + redeemed になり売上集計にも計上される。
  onsiteExperienceCardCharge: async (
    storeId: string,
    productId: string,
    // ticketId (#2188): 同じ販売の 2 回目なら 1 回目のチケット。それを払った決済があれば、新しく作られない
    spec: { quantity: number; slotDate?: string; slotStartTime?: string; ticketId?: string },
  ): Promise<{ paymentIntentId: string; readerId: string; amount: number; ticketId?: string; quantity: number;
    resumed?: boolean; unseen?: boolean }> => {
    const res = await apiFetch(
      `/experience/store/${encodeURIComponent(storeId)}/products/${encodeURIComponent(productId)}/onsite-card-charge`,
      // #2188 screenAcks: この画面は結果を受け取ったら /ack を送る (TerminalChargeModal)
      jsonPost({ ...spec, screenAcks: true }),
    )
    return res.json()
  },

  setStoreExperienceSlotOnline: async (storeId: string, productId: string, spec: { slotDate: string; slotStartTime: string; onlineOpen: boolean }): Promise<{ ok: boolean; onlineOpen: boolean }> => {
    const res = await apiFetch(`/experience/store/${encodeURIComponent(storeId)}/products/${encodeURIComponent(productId)}/slots/online`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(spec),
    })
    return res.json()
  },

  // #1303 店舗都合の催行中止 (悪天候等)。対象回の issued 券を一括キャンセル —
  // 与信は解放・即時売上は全額返金 (Stripe 手数料は店舗負担)。オンライン受付も閉じる。
  // failed に残った券は issued のまま — もう一度呼べば残りだけ処理される。
  cancelStoreExperienceSlot: async (
    storeId: string,
    productId: string,
    spec: { slotDate: string; slotStartTime: string; reason: string },
  ): Promise<{ released: number; refunded: number; failed: string[] }> => {
    const res = await apiFetch(
      `/experience/store/${encodeURIComponent(storeId)}/products/${encodeURIComponent(productId)}/slots/cancel`,
      jsonPost(spec),
    )
    return res.json()
  },

  // #1483 送迎の進み具合。ガイドが「迎えに行く」「到着」を押すと客の画面が変わる。
  // 認可は owner/manager でなく全ロール — **実際に運転するのは現場のガイド**。
  setExperienceTransportStatus: async (
    storeId: string,
    ticketId: string,
    spec: { status: 'heading' | 'arrived'; etaMinutes?: number; assigneeId?: string; assigneeName?: string; vehiclePlate?: string },
  ): Promise<ExperienceTicket> => {
    const res = await apiFetch(
      `/experience/store/${encodeURIComponent(storeId)}/tickets/${encodeURIComponent(ticketId)}/transport`,
      jsonPost(spec),
    )
    return res.json()
  },

  // #1483 ガイドの当日リスト。枠一覧は「何席売れたか」しか持たないが、送迎は
  // 一人ずつ迎えに行くので券単位で「誰の分をどこまで進めたか」が要る。
  listExperienceTransportTickets: async (storeId: string, productId: string, date: string): Promise<{ tickets: TransportTicket[] }> => {
    const res = await apiFetch(
      `/experience/store/${encodeURIComponent(storeId)}/products/${encodeURIComponent(productId)}/transport-tickets?date=${encodeURIComponent(date)}`,
    )
    return res.json()
  },

  // #1483 予約時刻を過ぎてもガイドが動いていない券を無料キャンセルする安全弁。
  // 当日運営画面を開いたときに呼ぶ (予約の auto-expire と同じ lazy cleanup)。
  expireUnattendedTransport: async (storeId: string, productId: string): Promise<{ cancelled: number }> => {
    const res = await apiFetch(
      `/experience/store/${encodeURIComponent(storeId)}/products/${encodeURIComponent(productId)}/expire-unattended`,
      { method: 'POST' },
    )
    return res.json()
  },

  // 売上サマリ (本日/累計の販売枚数・金額)。時間指定なし商品の枚数/金額管理にも使う。
  getStoreExperienceSalesSummary: async (storeId: string, productId: string): Promise<ExperienceSalesSummary> => {
    const res = await apiFetch(`/experience/store/${encodeURIComponent(storeId)}/products/${encodeURIComponent(productId)}/sales-summary`)
    return res.json()
  },
}

/** #1292: オーナー画面の体験商品 (公開 view + isActive) */
export interface OwnerExperienceProduct extends ExperienceProduct {
  isActive: boolean
}

/** #1292: 体験商品の作成/更新入力 (店舗オーナー) */
export interface ExperienceProductInput {
  name: string
  nameEn?: string
  description?: string
  descriptionEn?: string
  imageUrl?: string
  priceJpy: number
  genre?: string
  linkedSpotId?: string | null
  redeemPolicy?: string   // free_entry | timed
  /** #1482 送迎付きか */
  transportIncluded?: boolean
  /** #1487 所要時間(分) / 滞在自由か */
  durationMinutes?: number | null
  openEnded?: boolean
  /** #1522 集合場所の候補。id は新規行では無し (サーバが採番する) */
  meetingPoints?: (Omit<MeetingPoint, 'id'> & { id?: string })[]
  isActive?: boolean
}

/** #1292: 週次スケジュールの1曜日 (on/off + 営業時間) */
export interface ScheduleDay {
  enabled: boolean
  start: string   // HH:MM
  end: string     // HH:MM
}

/** #1292: 商品の週次スケジュール。days のキーは "0"=日 .. "6"=土 */
export interface ExperienceSchedule {
  slotMinutes: number
  capacity: number
  days: Record<string, ScheduleDay>
}

/** #1292 Phase 2: 店舗運営ビューの枠 (定員/予約済/残り/オンライン受付) */
export interface StoreExperienceSlot {
  date: string
  startTime: string
  /** #1487 終了時刻 HH:MM。滞在自由の商品は null */
  endTime?: string | null
  capacity: number
  sold: number
  remaining: number
  onlineOpen: boolean
}

/** #1292 Phase 2: 売上サマリ (本日/累計の販売枚数・金額) */
export interface ExperienceSalesSummary {
  todayQty: number
  todayRevenue: number
  totalQty: number
  totalRevenue: number
}

/** #1483 ガイドの当日リスト 1 行。送迎は一人ずつ迎えに行くので券単位で持つ。 */
export interface TransportTicket {
  id: string
  // code は意図的に無い: 合流 (= 請求の確定) には客の端末の QR が要る (#1520 follow-up)
  /** issued = 未合流 / redeemed = 合流済み */
  status?: string | null
  slotStartTime: string | null
  quantity: number
  /** heading → arrived → boarded (#1521 合流 = QR 読み取り) */
  transportStatus?: string | null
  transportEtaMinutes?: number | null
  /** #1522 到着予定時刻 (ISO) / 客が選んだ集合場所 */
  transportEtaAt?: string | null
  meetingPoint?: MeetingPoint | null
  /** #1483 実際に運転する担当者。押した人 (アカウント) とは別 */
  transportAssigneeName?: string | null
}
